import { SloDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import React, { FC, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/services';
import { joinEventsToSlo } from '@/ui';
import { SloEventsFromSingleSLO } from '@/api';
import { applySloDefaults } from '@/views/slo-view/utils';
import { useFetchErrorController } from '@/controllers';
import { querySpec } from '@/services/api-service/api-queries';
import { SLOCard } from '@/components/composed';
import GroupCard from '@/components/composed/slo-card/group-card';

import SearchPageSkeleton from '../search-page-skeleton';

import * as styles from './styles';

type SearchListSLOCardProps = {
  id: string;
  isGroup?: boolean;
};

const SearchListSLOCard: FC<SearchListSLOCardProps> = ({ id, isGroup }) => {
  const { onError } = useFetchErrorController();

  const { queryKey: slosKey, path: slosPath } = querySpec(`${isGroup ? 'slo-group' : 'slo'}.{id}`, {
    id,
  });
  const { data: rawSloData, isInitialLoading: isLoadingSlo } = useQuery(
    slosKey,
    () => apiFetch(slosPath),
    { onError }
  );

  const { queryKey: eventsKey, path: eventsPath } = querySpec('slo.{id}.events', { id });
  const { data: rawEventData, isInitialLoading: isLoadingEvents } = useQuery(
    eventsKey,
    () => apiFetch(eventsPath),
    { onError, enabled: !isGroup }
  );

  const { queryKey: sloIncidentsKey, path: sloIncidentsPath } = querySpec('hot.slo.{id}', { id });
  const { data: sloIncidentsData, isInitialLoading: isLoadingIncidents } = useQuery(
    sloIncidentsKey,
    () => apiFetch(sloIncidentsPath),
    { onError, enabled: !isGroup }
  );

  const data = useMemo(
    () =>
      rawSloData &&
      joinEventsToSlo(
        SloEventsFromSingleSLO(rawEventData ?? {}),
        sloIncidentsData || [],
        applySloDefaults(SloDTOFromJSON(rawSloData))
      ),
    [rawSloData, rawEventData, sloIncidentsData]
  );

  const isLoading = isLoadingSlo || isLoadingIncidents || isLoadingEvents;

  if (isLoading)
    return (
      <div css={styles.cardSkeletonStyles}>
        <SearchPageSkeleton isCompact />
      </div>
    );

  if (!data) return null;
  return (
    <div css={[styles.cardSloContentStyles, isGroup ? styles.cardSloGroupContentStyles : '']}>
      {isGroup && <GroupCard {...data} isCompact isResponsive isLarge />}
      {!isGroup && <SLOCard {...data} isCompact isResponsive />}
    </div>
  );
};

export default SearchListSLOCard;
